import NextLink from 'next/link';
import PropTypes from 'prop-types';

import { isValidPill } from 'lib/homePills';
import trackEvent from 'lib/posthog/trackEvent';

const DEFAULT_BG = '#333333';
const MAX_TILES = 5;
const GRID_COLS = [
  '',
  'lg:grid-cols-1',
  'lg:grid-cols-2',
  'lg:grid-cols-3',
  'lg:grid-cols-4',
  'lg:grid-cols-5',
];

// Fallback used when NEXT_PUBLIC_HOME_PILLS_CONFIG_URL is absent, S3 fetch fails, or returns empty.
export const DEFAULT_PILLS = [
  {
    url: '/cupons/loja/mercado-livre/',
    logo: 'https://i.promobit.com.br/generic-upload/691635793617846366424673957605_generic.webp',
    bg: '#FFE600',
    identifier: 'mercado-livre',
  },
  {
    url: '/cupons/loja/amazon/',
    logo: 'https://i.promobit.com.br/generic-upload/380499111917846367443010304627_generic.webp',
    bg: '#222222',
    identifier: 'amazon',
  },
  {
    url: '/cupons/loja/shopee/',
    logo: 'https://i.promobit.com.br/generic-upload/418123822417846368383819377716_generic.webp',
    bg: '#F6412E',
    identifier: 'shopee',
  },
];

const HomePills = ({ stores, title }) => {
  const validStores = (stores ?? []).filter(isValidPill).slice(0, MAX_TILES);

  if (validStores.length === 0) return null;

  const colsClass = GRID_COLS[validStores.length] ?? 'lg:grid-cols-5';

  return (
    <div className="mb-4">
      {title && (
        <p className="mb-3 text-lg font-medium text-neutral-low-300 dark:text-neutral-high-300 lg:text-base">
          {title}
        </p>
      )}
      <div
        className={`flex snap-x gap-2 overflow-x-auto scrollbar-none lg:grid lg:gap-4 ${colsClass} -mx-1 -my-2 px-1 py-2`}
      >
        {validStores.map((entry, idx) => {
          const {
            url,
            logo,
            text,
            bg,
            identifier,
            is_highlighted: isHighlighted,
          } = entry;
          const trackingId = identifier || url;
          const reactKey = trackingId || idx;

          return (
            <NextLink key={reactKey} href={url} passHref>
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- href provided via NextLink passHref; linter cannot see it statically */}
              <a
                className={[
                  'relative flex h-[44px] min-w-[120px] flex-1 cursor-pointer items-center justify-center rounded-[8px] p-2.5 transition-opacity hover:opacity-90 lg:h-[56px] lg:min-w-0 lg:p-3',
                ].join(' ')}
                style={{ backgroundColor: bg || DEFAULT_BG }}
                onClick={() =>
                  trackEvent(
                    'click home pill',
                    { identifier: trackingId },
                    { destination: 'posthog' }
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    trackEvent(
                      'click home pill',
                      { identifier: trackingId },
                      { destination: 'posthog' }
                    );
                  }
                }}
              >
                {isHighlighted && (
                  <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-[8px]">
                    <span
                      className="absolute bottom-0 left-0 top-0 w-[60%] animate-pill-shine"
                      style={{
                        background:
                          'linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.6) 50%, transparent 80%)',
                      }}
                    />
                  </span>
                )}
                {logo ? (
                  <img
                    alt={text || identifier || ''}
                    className="max-h-[26px] w-auto object-contain lg:max-h-[32px]"
                    src={logo}
                  />
                ) : (
                  <span className="text-center text-[13px] font-semibold leading-tight text-neutral-high-100 lg:text-[15px]">
                    {text}
                  </span>
                )}
              </a>
            </NextLink>
          );
        })}
      </div>
    </div>
  );
};

HomePills.defaultProps = {
  stores: DEFAULT_PILLS,
  title: null,
};

HomePills.propTypes = {
  title: PropTypes.string,
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      bg: PropTypes.string,
      logo: PropTypes.string,
      text: PropTypes.string,
      identifier: PropTypes.string,
      is_highlighted: PropTypes.bool,
      starts_at: PropTypes.string,
      ends_at: PropTypes.string,
    })
  ),
};

export default HomePills;
